import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Loader2, Megaphone, ImagePlus, X, CheckCircle2,
  ShieldCheck, Film, PenLine, Clapperboard, Mic2, Wand2, Lock, KeyRound,
  Smartphone, Square, Monitor, Download, ArrowUpRight,
} from "lucide-react";
import { pipelinesApi, streamAsset } from "@/api/mist";
import { AssetPicker } from "@/components/mist/shared";

const TONES = [
  "Bold & Punchy", "Premium & Luxurious", "Friendly & Casual",
  "Professional & Trustworthy", "Playful & Fun", "Inspirational & Uplifting",
];

const DURATIONS = [
  { v: 15, label: "15s", note: "Default" },
  { v: 30, label: "30s" },
  { v: 60, label: "60s" },
];

const ASPECTS = [
  { v: "9:16", label: "Vertical", emoji: "📱", Icon: Smartphone },
  { v: "1:1", label: "Square", emoji: "🟦", Icon: Square },
  { v: "16:9", label: "Widescreen", emoji: "🖥️", Icon: Monitor },
];

const ASPECT_CLASS = { "9:16": "aspect-[9/16]", "1:1": "aspect-square", "16:9": "aspect-video" };

const VOICES = [
  { id: "", label: "Auto (recommended)" },
  { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel — calm female" },
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam — deep male" },
  { id: "ErXwobaYiN019PkySvjV", label: "Antoni — warm male" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah — soft female" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh — energetic male" },
  { id: "AZnzlk1XvdvUeBnXmlld", label: "Domi — confident female" },
];

const VIBES = [
  { key: "High-Energy Pop", emoji: "🔥" },
  { key: "Tech Synth", emoji: "💻" },
  { key: "Aggressive Trap", emoji: "🏎️" },
  { key: "Chill Lofi", emoji: "☕" },
];

const STAGES = {
  queued:  { label: "Preparing your ad brief…", Icon: Sparkles, pct: 8 },
  script:  { label: "Alix is writing your conversion script…", Icon: PenLine, pct: 28 },
  scenes:  { label: "Generating your ad scenes…", Icon: Clapperboard, pct: 55 },
  audio:   { label: "Recording voiceover & mixing the music bed…", Icon: Mic2, pct: 78 },
  stitch:  { label: "Stitching your reel & appending your outro…", Icon: Film, pct: 92 },
  done:    { label: "Done!", Icon: CheckCircle2, pct: 100 },
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block mb-1 font-mono text-[9px] font-black uppercase tracking-[0.14em] text-charcoal/55">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full border-2 border-charcoal bg-white text-charcoal px-3 py-2.5 font-sans text-sm rounded-lg outline-none focus:shadow-[3px_3px_0px_#1A1A1A] transition-shadow";

export default function AdMakerWorkspace({ onBack }) {
  const [form, setForm] = useState({
    name: "", niche: "", primary_offer: "", target_audience: "", brand_tone: TONES[0],
    target_duration: 15, voice_id: "", music_vibe: VIBES[0].key,
    aspect_preset: "9:16", generate_variations: false,
  });
  const [logoSel, setLogoSel] = useState([]);          // AssetPicker filenames
  const [logoItem, setLogoItem] = useState(null);      // resolved {id, filename}
  const [charSel, setCharSel] = useState([]);
  const [charItem, setCharItem] = useState(null);
  const [access, setAccess] = useState(null);            // null=loading

  const [jobId, setJobId] = useState(null);
  const [stage, setStage] = useState(null);
  const [status, setStatus] = useState("idle");          // idle | processing | completed | failed
  const [results, setResults] = useState([]);            // [{asset_id, variant, url}]
  const [publishFor, setPublishFor] = useState(null);    // asset being published
  const [publishCaption, setPublishCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishedIds, setPublishedIds] = useState([]);
  const pollRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    pipelinesApi.adMakerAccess().then(setAccess).catch(() => setAccess({ eligible: true, keys_ready: true, is_founder_exempt: false }));
    pipelinesApi.businessProfile().then((d) => {
      if (d?.profile) {
        setForm((f) => ({
          ...f,
          name: d.profile.name || "",
          niche: d.profile.niche || "",
          primary_offer: d.profile.primary_offer || "",
          target_audience: d.profile.target_audience || "",
          brand_tone: d.profile.brand_tone || TONES[0],
          target_duration: d.profile.target_duration || 15,
          voice_id: d.profile.voice_id || "",
          music_vibe: d.profile.music_vibe || VIBES[0].key,
          aspect_preset: d.profile.aspect_preset || "9:16",
          generate_variations: !!d.profile.generate_variations,
        }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Hide the founder PiP test-feed on mobile while this editor is open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const generate = useCallback(async () => {
    if (!form.name.trim()) { toast.error("Add your business name first"); return; }
    stopPoll();
    setStatus("processing"); setStage("queued"); setResults([]); setJobId(null);
    try {
      const { job_id } = await pipelinesApi.generateAd({
        ...form,
        logo_asset_id: logoItem?.id || null,
        character_asset_id: charItem?.id || null,
      });
      setJobId(job_id);
      pollRef.current = setInterval(async () => {
        try {
          const j = await pipelinesApi.job(job_id);
          if (j.stage) setStage(j.stage);
          if (j.status === "completed" && (j.assets?.length || j.asset_id)) {
            stopPoll(); setStage("done");
            const list = j.assets?.length ? j.assets : [{ asset_id: j.asset_id, variant: null }];
            const withUrls = await Promise.all(list.map(async (a) => ({
              ...a, url: await streamAsset(a.asset_id).catch(() => null),
            })));
            setResults(withUrls.filter((a) => a.url));
            setStatus("completed");
            toast.success(withUrls.length > 1 ? `${withUrls.length} ad variants ready ✓` : "Your ad reel is ready ✓");
          } else if (j.status === "failed") {
            stopPoll(); setStatus("failed");
            toast.error(j.error || "Ad generation failed");
          }
        } catch { /* transient poll error — keep trying */ }
      }, 4000);
    } catch (e) {
      setStatus("failed");
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not start the ad generator"));
    }
  }, [form, logoItem, charItem]);

  const doPublish = async () => {
    if (!publishFor) return;
    setPublishing(true);
    try {
      await pipelinesApi.publishAdVariant({ asset_id: publishFor, custom_caption: publishCaption });
      setPublishedIds((p) => [...p, publishFor]);
      setPublishFor(null); setPublishCaption("");
      toast.success("Published to your public feed ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally {
      setPublishing(false);
    }
  };

  const busy = status === "processing";
  const st = STAGES[stage] || STAGES.queued;
  const StageIcon = st.Icon;
  const eligible = access ? access.eligible : true;
  const founderExempt = access?.is_founder_exempt;
  const keysReady = access ? access.keys_ready : true;
  const byokBlocked = eligible && !founderExempt && !keysReady;
  const missingKeys = access?.missing_keys || [];
  const aspectCls = ASPECT_CLASS[form.aspect_preset] || "aspect-[9/16]";

  // ---- State A: retail user is not permitted to use the Ad Maker ----
  if (access && !eligible) {
    return (
      <div className="absolute inset-0 z-40 flex flex-col bg-cream" data-testid="ad-maker-workspace">
        <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 bg-cream border-b-2 border-charcoal/10">
          <button onClick={onBack} data-testid="ad-maker-back" aria-label="Back"
            className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <ArrowLeft size={18} strokeWidth={2.4} />
          </button>
          <h1 className="font-serif text-2xl text-charcoal">Ad Maker</h1>
        </header>
        <div className="flex-1 grid place-items-center px-6" data-testid="ad-locked">
          <div className="max-w-sm text-center space-y-5 border-2 border-charcoal rounded-2xl bg-white p-7 shadow-[4px_4px_0px_#1A1A1A]">
            <span className="mx-auto w-16 h-16 rounded-2xl border-2 border-charcoal grid place-items-center bg-[#4F46E5] shadow-[3px_3px_0px_#1A1A1A]">
              <Lock size={28} className="text-white" strokeWidth={2.3} />
            </span>
            <h2 className="font-serif text-2xl text-charcoal">💼 Commercial Access Only</h2>
            <p className="font-sans text-sm text-charcoal/70 leading-relaxed">
              The Ad Maker suite is a high-performance engine restricted to verified Business Profiles and Professional Creators.
            </p>
            <button onClick={() => { window.location.href = "/?life=wealth"; }} data-testid="ad-upgrade-btn"
              className="w-full flex items-center justify-center gap-2 bg-charcoal text-white font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-charcoal rounded-xl shadow-[3px_3px_0px_#4F46E5] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <ArrowUpRight size={16} /> Upgrade Profile Type
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-cream" data-testid="ad-maker-workspace">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 bg-cream border-b-2 border-charcoal/10">
        <button onClick={onBack} data-testid="ad-maker-back" aria-label="Back"
          className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} strokeWidth={2.4} />
        </button>
        <div className="leading-tight min-w-0 flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl border-2 border-charcoal grid place-items-center bg-[#4F46E5] shadow-[2px_2px_0px_#1A1A1A]">
            <Megaphone size={18} className="text-white" strokeWidth={2.3} />
          </span>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl text-charcoal truncate">Ad Maker</h1>
            <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-charcoal/50 truncate">Business & Tools · Marketing</p>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-28">
        <div className="grid md:grid-cols-12 gap-4">
          {/* -------- LEFT: profile inputs -------- */}
          <div className="md:col-span-5 space-y-3" data-testid="ad-maker-form">
            <div className="border-2 border-charcoal rounded-2xl bg-white p-4 shadow-[3px_3px_0px_#1A1A1A] space-y-3">
              <Field label="Business Name">
                <input value={form.name} onChange={(e) => set("name", e.target.value)}
                  data-testid="ad-name" placeholder="e.g. Nova Coffee Co." className={inputCls} />
              </Field>
              <Field label="Niche">
                <input value={form.niche} onChange={(e) => set("niche", e.target.value)}
                  data-testid="ad-niche" placeholder="e.g. specialty coffee roastery" className={inputCls} />
              </Field>
              <Field label="Primary Offer">
                <input value={form.primary_offer} onChange={(e) => set("primary_offer", e.target.value)}
                  data-testid="ad-offer" placeholder="e.g. 20% off your first subscription box" className={inputCls} />
              </Field>
              <Field label="Target Audience">
                <input value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)}
                  data-testid="ad-audience" placeholder="e.g. busy urban professionals 25-40" className={inputCls} />
              </Field>
              <Field label="Brand Tone">
                <select value={form.brand_tone} onChange={(e) => set("brand_tone", e.target.value)}
                  data-testid="ad-tone" className={inputCls}>
                  {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <Field label="Ad Target Length">
                <div className="grid grid-cols-3 gap-2" data-testid="ad-duration-group">
                  {DURATIONS.map((d) => (
                    <button key={d.v} type="button" onClick={() => set("target_duration", d.v)}
                      data-testid={`ad-duration-${d.v}`}
                      className={`flex flex-col items-center justify-center border-2 border-charcoal rounded-lg py-2 font-mono font-black uppercase transition-colors ${form.target_duration === d.v ? "bg-[#4F46E5] text-white shadow-[3px_3px_0px_#1A1A1A]" : "bg-white text-charcoal/60"}`}>
                      <span className="text-sm">{d.label}</span>
                      {d.note && <span className="text-[7px] tracking-widest opacity-70">{d.note}</span>}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Brand Voice">
                <select value={form.voice_id} onChange={(e) => set("voice_id", e.target.value)}
                  data-testid="ad-voice" className={inputCls}>
                  {VOICES.map((v) => <option key={v.id || "auto"} value={v.id}>{v.label}</option>)}
                </select>
              </Field>

              <Field label="Background Track Vibe">
                <div className="grid grid-cols-2 gap-2" data-testid="ad-vibe-group">
                  {VIBES.map((vb) => (
                    <button key={vb.key} type="button" onClick={() => set("music_vibe", vb.key)}
                      data-testid={`ad-vibe-${vb.key.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`flex items-center gap-1.5 border-2 border-charcoal rounded-lg px-2.5 py-2 font-mono text-[10px] font-black uppercase text-left transition-colors ${form.music_vibe === vb.key ? "bg-[#4F46E5] text-white shadow-[3px_3px_0px_#1A1A1A]" : "bg-white text-charcoal/60"}`}>
                      <span className="text-sm leading-none">{vb.emoji}</span>
                      <span className="leading-tight">{vb.key}</span>
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Placement Layout">
                <div className="grid grid-cols-3 gap-2" data-testid="ad-aspect-group">
                  {ASPECTS.map((a) => (
                    <button key={a.v} type="button" onClick={() => set("aspect_preset", a.v)}
                      data-testid={`ad-aspect-${a.v.replace(":", "-")}`}
                      className={`flex flex-col items-center gap-0.5 border-2 border-charcoal rounded-lg py-2 font-mono text-[9px] font-black uppercase transition-colors ${form.aspect_preset === a.v ? "bg-[#4F46E5] text-white shadow-[3px_3px_0px_#1A1A1A]" : "bg-white text-charcoal/60"}`}>
                      <span className="text-base leading-none">{a.emoji}</span>
                      <span>{a.label}</span>
                      <span className="opacity-60">{a.v}</span>
                    </button>
                  ))}
                </div>
              </Field>

              <button type="button" onClick={() => set("generate_variations", !form.generate_variations)}
                data-testid="ad-variations-toggle"
                className={`w-full flex items-center justify-between gap-3 border-2 border-charcoal rounded-lg px-3 py-2.5 transition-colors ${form.generate_variations ? "bg-[#4F46E5]/10" : "bg-white"}`}>
                <span className="text-left font-mono text-[10px] font-black uppercase tracking-wide text-charcoal/75 leading-tight">
                  Generate 3 Alternate Hooks<br /><span className="text-[8px] text-charcoal/45">for A/B testing</span>
                </span>
                <span className={`shrink-0 w-11 h-6 rounded-full border-2 border-charcoal relative transition-colors ${form.generate_variations ? "bg-[#4F46E5]" : "bg-charcoal/15"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white border border-charcoal transition-all ${form.generate_variations ? "left-[22px]" : "left-0.5"}`} />
                </span>
              </button>

              <Field label="Business Logo (optional)">
                <AssetPicker track="image" selected={logoSel} onChange={setLogoSel}
                  onItems={(items) => setLogoItem(items[0] || null)} max={1} testid="ad-logo-picker" />
              </Field>

              <Field label="Character Reference (optional)">
                <AssetPicker track="image" selected={charSel} onChange={setCharSel}
                  onItems={(items) => setCharItem(items[0] || null)} max={1} testid="ad-char-picker" />
              </Field>
            </div>

            {byokBlocked && (
              <div className="border-2 border-amber-500 bg-amber-50 rounded-xl p-3 flex gap-2.5" data-testid="ad-byok-warning">
                <KeyRound size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="font-mono text-[10px] leading-relaxed text-amber-800">
                  🔒 <b>Connect your account to unlock</b>: Please link your personal{" "}
                  {missingKeys.length ? missingKeys.join(" & ") : "fal.ai and ElevenLabs"} API keys in your
                  Account Registry to unlock premium rendering. <i>(Founders Exempt)</i>
                </p>
              </div>
            )}

            <button onClick={generate} disabled={busy || byokBlocked} data-testid="ad-generate-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#4F46E5] text-white font-black font-mono text-xs uppercase tracking-widest py-4 border-2 border-charcoal rounded-xl shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? <Loader2 size={16} className="animate-spin" /> : byokBlocked ? <Lock size={16} /> : <Wand2 size={16} />}
              {busy ? "Generating…" : byokBlocked ? "Link Keys to Unlock" : "Generate High-Converting Ad Reel"}
            </button>
          </div>

          {/* -------- RIGHT: pipeline / result -------- */}
          <div className="md:col-span-7" data-testid="ad-maker-output">
            <div className="border-2 border-charcoal rounded-2xl bg-white p-5 shadow-[3px_3px_0px_#1A1A1A] min-h-[420px] flex flex-col">
              {status === "idle" && (
                <div className="flex-1 grid place-items-center text-center" data-testid="ad-idle">
                  <div className="space-y-3 max-w-xs">
                    <span className="mx-auto w-14 h-14 rounded-2xl border-2 border-charcoal grid place-items-center bg-cream shadow-[3px_3px_0px_#1A1A1A]">
                      <Clapperboard size={26} className="text-[#4F46E5]" strokeWidth={2.2} />
                    </span>
                    <h3 className="font-serif text-xl text-charcoal">Your ad reel appears here</h3>
                    <p className="font-mono text-[11px] text-charcoal/55 leading-relaxed">
                      Fill in your brand details and tap generate. Alix writes a 3-part hook · body · CTA script,
                      renders each scene, records a voiceover over a ducked music bed, and appends your default outro.
                    </p>
                  </div>
                </div>
              )}

              {busy && (
                <div className="flex-1 grid place-items-center" data-testid="ad-progress">
                  <div className="w-full max-w-sm space-y-5 text-center">
                    <span className="mx-auto w-16 h-16 rounded-2xl border-2 border-charcoal grid place-items-center bg-cream shadow-[3px_3px_0px_#1A1A1A]">
                      <StageIcon size={28} className="text-[#4F46E5] animate-pulse" strokeWidth={2.2} />
                    </span>
                    <p className="font-sans font-bold text-[15px] text-charcoal" data-testid="ad-stage-label">{st.label}</p>
                    <div className="w-full h-3 border-2 border-charcoal rounded-full overflow-hidden bg-cream">
                      <div className="h-full bg-[#4F46E5] transition-all duration-700 ease-out" style={{ width: `${st.pct}%` }} data-testid="ad-progress-bar" />
                    </div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-charcoal/45">This can take a few minutes — you can stay on this screen</p>
                  </div>
                </div>
              )}

              {status === "completed" && results.length > 0 && (
                <div className="flex-1 flex flex-col gap-4" data-testid="ad-result">
                  <div className={results.length > 1 ? "grid grid-cols-1 sm:grid-cols-3 gap-3" : "flex justify-center"}>
                    {results.map((r, i) => (
                      <div key={r.asset_id} className="flex flex-col gap-2" data-testid={`ad-result-card-${r.variant || i}`}>
                        {r.variant && (
                          <span className="self-center font-mono text-[9px] font-black uppercase tracking-widest bg-[#4F46E5] text-white border-2 border-charcoal rounded-full px-3 py-0.5">
                            Variant {r.variant}
                          </span>
                        )}
                        <div className={`mx-auto w-full ${results.length > 1 ? "" : "max-w-[300px]"} border-4 border-charcoal rounded-2xl overflow-hidden shadow-[4px_4px_0px_#1A1A1A] bg-black`}>
                          <video src={r.url} controls loop playsInline autoPlay={i === 0}
                            className={`w-full ${aspectCls} object-contain bg-black`} data-testid={`ad-video-${r.variant || i}`} />
                        </div>
                        <a href={r.url} download={`ad_${r.variant || i}.mp4`} data-testid={`ad-download-${r.variant || i}`}
                          className="self-center flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wide text-charcoal border-2 border-charcoal rounded-full px-3 py-1 hover:bg-charcoal hover:text-white transition-colors">
                          <Download size={11} /> Download
                        </a>
                        <button onClick={() => { setPublishFor(r.asset_id); setPublishCaption(""); }}
                          disabled={publishedIds.includes(r.asset_id)}
                          data-testid={`ad-publish-${r.variant || i}`}
                          className="self-center flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wide bg-[#4F46E5] text-white border-2 border-charcoal rounded-full px-3 py-1 shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                          {publishedIds.includes(r.asset_id)
                            ? <><CheckCircle2 size={11} /> Posted</>
                            : <>📢 Post to Public Feed</>}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <span className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wide text-emerald-700 bg-emerald-50 border-2 border-emerald-700/40 rounded-full px-3 py-1">
                      <ShieldCheck size={12} /> Safe-zone enforced
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wide text-[#4F46E5] bg-[#4F46E5]/10 border-2 border-[#4F46E5]/40 rounded-full px-3 py-1">
                      <Film size={12} /> Outro appended · {form.aspect_preset}
                    </span>
                  </div>
                  <p className="text-center font-mono text-[10px] text-charcoal/50">Saved to your vault. Generate another anytime.</p>
                </div>
              )}

              {status === "failed" && (
                <div className="flex-1 grid place-items-center text-center" data-testid="ad-failed">
                  <div className="space-y-3">
                    <p className="font-sans font-bold text-charcoal">That didn’t complete.</p>
                    <p className="font-mono text-[11px] text-charcoal/55">Please try again in a moment.</p>
                    <button onClick={generate} className="font-mono text-[10px] font-black uppercase tracking-widest bg-charcoal text-white px-4 py-2 rounded-lg border-2 border-charcoal">Retry</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {publishFor && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-charcoal/60 backdrop-blur-sm px-6" data-testid="ad-publish-modal">
          <div className="w-full max-w-sm border-2 border-charcoal rounded-2xl bg-cream p-5 shadow-[5px_5px_0px_#1A1A1A] space-y-3">
            <div className="flex items-center gap-2">
              <Megaphone size={18} className="text-[#4F46E5]" />
              <h3 className="font-serif text-xl text-charcoal">Post to Public Feed</h3>
            </div>
            <p className="font-mono text-[10px] text-charcoal/55 leading-relaxed">Add an optional caption. Your business name and offer are attached automatically.</p>
            <textarea value={publishCaption} onChange={(e) => setPublishCaption(e.target.value)}
              data-testid="ad-publish-caption" rows={3} placeholder="Write a caption (optional)…"
              className="w-full border-2 border-charcoal bg-white text-charcoal px-3 py-2 font-sans text-sm rounded-lg outline-none focus:shadow-[3px_3px_0px_#1A1A1A] resize-none" />
            <div className="flex gap-2">
              <button onClick={() => { setPublishFor(null); setPublishCaption(""); }} data-testid="ad-publish-cancel"
                className="flex-1 font-mono text-[10px] font-black uppercase tracking-widest border-2 border-charcoal rounded-xl py-2.5 text-charcoal/60 bg-white">Cancel</button>
              <button onClick={doPublish} disabled={publishing} data-testid="ad-publish-submit"
                className="flex-1 flex items-center justify-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest bg-[#4F46E5] text-white border-2 border-charcoal rounded-xl py-2.5 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
                {publishing ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
